import {
  ScanLine,
  CreditCard,
  MonitorPlay,
  Ticket,
  Network,
  ArrowUpRight,
  ShieldCheck,
  Wrench,
  Award,
  Plug,
} from "lucide-react";

const HARDWARE_LINES = [
  {
    id: "hw-autoservicio",
    num: "01",
    name: "Autoservicio",
    Icon: ScanLine,
    equipos: [
      "Kioscos de autoatención",
      "Pantallas táctiles industriales",
      "Lectores de código de barras / QR",
      "Impresoras de comprobante",
    ],
    tags: ["Indoor", "Outdoor", "Soporte de pie"],
  },
  {
    id: "hw-pos",
    num: "02",
    name: "POS · Puntos de Venta",
    Icon: CreditCard,
    equipos: [
      "Terminales POS fijos y móviles",
      "Impresoras térmicas",
      "Cajones monederos",
      "Lectores 1D / 2D",
    ],
    tags: ["Retail", "Hospitality", "Supermercado"],
  },
  {
    id: "hw-carteleria",
    num: "03",
    name: "Cartelería Digital",
    Icon: MonitorPlay,
    equipos: [
      "Pantallas LCD profesionales",
      "Pantallas LED indoor / outdoor",
      "Reproductores de señalética",
      "Video walls modulares",
    ],
    tags: ["24/7", "Multi-sucursal"],
  },
  {
    id: "hw-turnomaticos",
    num: "04",
    name: "Turnomáticos",
    Icon: Ticket,
    equipos: [
      "Dispensadores de tickets",
      "Pantallas LED de llamado",
      "Módulos de operador",
      "Sistema de gestión central",
    ],
    tags: ["Multi-cola", "Métricas en vivo"],
  },
  {
    id: "hw-redes",
    num: "05",
    name: "Redes & Fibra Óptica",
    Icon: Network,
    equipos: [
      "Cable UTP Cat 6 / 6A blindado",
      "Fibra óptica monomodo y multimodo",
      "Switches gestionados y patch panels",
      "Equipos de certificación calibrados",
    ],
    tags: ["Cat 6 / 6A", "Monomodo", "Multimodo", "Indoor / Outdoor"],
  },
];

const VALUE_PROPS = [
  {
    Icon: Award,
    title: "Marcas líderes",
    text: "Equipos certificados de fabricantes de primera línea.",
  },
  {
    Icon: ShieldCheck,
    title: "Garantía extendida",
    text: "Cobertura post-venta y reemplazo en sitio.",
  },
  {
    Icon: Plug,
    title: "Llave en mano",
    text: "Despacho, instalación, integración y puesta en marcha.",
  },
  {
    Icon: Wrench,
    title: "Repuestos & SAT",
    text: "Servicio técnico autorizado y stock de repuestos.",
  },
];

export const Hardware = () => {
  return (
    <section
      id="hardware"
      data-testid="hardware-section"
      className="relative bg-[#05162a] text-white py-24 md:py-32 overflow-hidden"
    >
      {/* Subtle grid bg */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.07] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(to right, rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.5) 1px, transparent 1px)",
          backgroundSize: "72px 72px",
          maskImage:
            "radial-gradient(ellipse 80% 70% at 50% 40%, black 50%, transparent 100%)",
          WebkitMaskImage:
            "radial-gradient(ellipse 80% 70% at 50% 40%, black 50%, transparent 100%)",
        }}
      />

      {/* Teal accent */}
      <div
        aria-hidden
        className="absolute top-1/3 -right-32 w-[480px] h-[480px] rounded-full bg-[#1FA08A]/15 blur-[140px]"
      />
      <div
        aria-hidden
        className="absolute bottom-0 -left-32 w-[420px] h-[420px] rounded-full bg-[#1FA08A]/10 blur-[120px]"
      />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        {/* Section header */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-end mb-16 md:mb-20">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-5 text-[11px] uppercase tracking-[0.32em] font-mono">
              <span className="text-[#2bc9ae] font-semibold">/ 03</span>
              <span className="h-px w-10 bg-white/25" />
              <span className="text-slate-300/70 font-semibold">
                Catálogo · Hardware
              </span>
            </div>
            <h2
              data-testid="hardware-heading"
              className="font-heading text-white text-4xl sm:text-5xl md:text-[56px] lg:text-[64px] leading-[1.02] tracking-tighter font-bold"
            >
              Hardware certificado.{" "}
              <span className="text-[#2bc9ae]">Operación</span> garantizada.
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p
              data-testid="hardware-subtitle"
              className="text-slate-300/85 text-base md:text-lg leading-relaxed max-w-md lg:ml-auto"
            >
              Vendemos, integramos y damos soporte a todos los equipos detrás
              de nuestras soluciones. Marcas líderes con respaldo técnico —{" "}
              <span className="text-white font-semibold">de Betec.</span>
            </p>
          </div>
        </div>

        {/* Editorial product rows */}
        <div
          data-testid="hardware-list"
          className="border-y border-white/10 divide-y divide-white/10"
        >
          {HARDWARE_LINES.map((line) => (
            <HardwareRow key={line.id} line={line} />
          ))}
        </div>

        {/* Value props strip */}
        <div className="mt-16 md:mt-20">
          <div className="text-[11px] uppercase tracking-[0.32em] font-mono text-[#2bc9ae] font-semibold mb-7">
            ¿Por qué comprar con Betec?
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-8">
            {VALUE_PROPS.map(({ Icon, title, text }, i) => (
              <div
                key={i}
                data-testid={`hardware-valueprop-${i}`}
                className="flex flex-col"
              >
                <div className="inline-flex items-center justify-center w-11 h-11 rounded-md bg-white/[0.06] border border-white/10 text-[#2bc9ae] mb-4">
                  <Icon className="w-5 h-5" strokeWidth={1.6} />
                </div>
                <div className="text-[15px] font-semibold text-white mb-1.5 font-heading">
                  {title}
                </div>
                <div className="text-[13px] text-slate-300/75 leading-relaxed">
                  {text}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 md:mt-20 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 bg-white/[0.04] border border-white/10 rounded-md px-6 md:px-8 py-6 md:py-7 backdrop-blur-sm">
          <div className="flex items-start md:items-center gap-4">
            <span className="relative flex h-2.5 w-2.5 mt-1.5 md:mt-0">
              <span className="absolute inline-flex h-full w-full rounded-full bg-[#2bc9ae] opacity-75 animate-ping" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#2bc9ae]" />
            </span>
            <div>
              <p className="text-[11px] uppercase tracking-[0.28em] text-[#2bc9ae] font-mono font-semibold mb-1.5">
                Stock disponible · Despacho nacional
              </p>
              <p className="text-white text-lg md:text-xl font-heading font-semibold tracking-tight">
                Solicita una cotización con condiciones B2B.
              </p>
            </div>
          </div>
          <a
            href="#contacto"
            data-testid="hardware-cta"
            className="cta-cotizar group inline-flex items-center gap-2.5 bg-[#1FA08A] hover:bg-[#2bc9ae] text-white px-7 py-3.5 rounded-sm font-semibold text-[14px] tracking-wide transition-all duration-300 hover:-translate-y-0.5 shadow-[0_10px_30px_-10px_rgba(31,160,138,0.6)] hover:shadow-[0_18px_50px_-10px_rgba(43,201,174,0.8)] whitespace-nowrap"
          >
            Solicitar cotización
            <span aria-hidden className="cta-dot" />
          </a>
        </div>
      </div>
    </section>
  );
};

const HardwareRow = ({ line }) => {
  const { id, num, name, Icon, equipos, tags } = line;
  return (
    <article
      data-testid={`hardware-row-${id}`}
      className="hw-row group relative py-8 md:py-10 grid grid-cols-12 gap-4 md:gap-8 items-start transition-colors duration-500 hover:bg-white/[0.025]"
    >
      {/* Hover left accent bar */}
      <span
        aria-hidden
        className="absolute left-0 top-0 bottom-0 w-[3px] bg-[#2bc9ae] scale-y-0 origin-top group-hover:scale-y-100 transition-transform duration-500"
      />

      {/* Number */}
      <div className="col-span-2 md:col-span-1 flex items-start">
        <span className="text-[#2bc9ae] font-mono text-[13px] md:text-[14px] tracking-[0.18em] font-semibold pt-1">
          / {num}
        </span>
      </div>

      {/* Title + icon */}
      <div className="col-span-10 md:col-span-4 flex items-start gap-4">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-md bg-white/[0.06] border border-white/10 text-[#2bc9ae] shrink-0 transition-all duration-500 group-hover:bg-[#1FA08A] group-hover:text-white group-hover:border-[#1FA08A]">
          <Icon className="w-6 h-6" strokeWidth={1.5} />
        </div>
        <div>
          <h3 className="font-heading text-white text-2xl md:text-[28px] font-semibold tracking-tight leading-tight">
            {name}
          </h3>
          <div className="mt-3 flex flex-wrap gap-1.5">
            {tags.map((t) => (
              <span
                key={t}
                className="inline-flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.16em] font-semibold text-slate-300/85 bg-white/[0.04] border border-white/10 px-2 py-0.5 rounded-sm"
              >
                <span className="w-1 h-1 rounded-full bg-[#2bc9ae]" />
                {t}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Equipment list */}
      <div className="col-span-12 md:col-span-6 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5 md:pt-2">
        {equipos.map((e, i) => (
          <div
            key={i}
            className="flex items-start gap-2.5 text-[14px] text-slate-200/85 leading-snug"
          >
            <span className="mt-2 w-1 h-1 rounded-full bg-[#2bc9ae] shrink-0" />
            <span>{e}</span>
          </div>
        ))}
      </div>

      {/* Arrow */}
      <div className="col-span-12 md:col-span-1 flex md:justify-end md:pt-2">
        <a
          href="#contacto"
          aria-label={`Cotizar ${name}`}
          className="inline-flex items-center justify-center w-10 h-10 rounded-sm border border-white/15 text-white/70 hover:text-white hover:border-[#2bc9ae] hover:bg-[#1FA08A] transition-all duration-300"
        >
          <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </a>
      </div>
    </article>
  );
};

export default Hardware;
